import React, { useState } from 'react';
import componentStyles from './parlayView.module.css';
import { formatPropMarketTypeLabel } from '../../utils/columnUtils';
import { getConfidenceColor, getConfidenceText } from '../betFeed/propBetRecommendation';
import ParlayBetSlipModal from './parlayBetSlipModal';

/**
 * ParlayView Component
 * 
 * Displays parlay betting combinations in two view modes:
 * - Simplified ticket view (default): Clean, easy-to-read format
 * - Normal view: Detailed view with all information
 * 
 * @param {Array} parlays - Array of parlay objects to display
 * @param {string} viewMode - 'simplified' or 'normal'
 * @param {function} formatDate - Function to format date strings
 */
const ParlayView = ({ parlays, viewMode = 'simplified', formatDate }) => {
    const [expandedParlay, setExpandedParlay] = useState(null);
    const [betSlipParlayIndex, setBetSlipParlayIndex] = useState(null);

    const normalizeMetaValue = (value) => {
        if (!value) return null;
        const trimmed = String(value).trim();
        if (!trimmed) return null;
        if (trimmed.toLowerCase() === 'unknown') return null;
        if (trimmed.toLowerCase() === 'n/a') return null;
        return trimmed;
    };

    const buildMetaParts = (bet) => {
        const type = normalizeMetaValue(bet.type);
        const sport = normalizeMetaValue(bet.sport);
        const date = normalizeMetaValue(formatDate ? formatDate(bet.date) : bet.date);
        return [type, sport, date].filter(Boolean);
    };

    // Extra scouting context for a leg: formatted prop market (when present) and confidence badge.
    const renderMarketAndConfidence = (bet) => {
        const marketLabel = bet.marketType ? formatPropMarketTypeLabel(bet.marketType) : null;
        const confidenceLabel = bet.confidence ? getConfidenceText(bet.confidence) : null;
        if (!marketLabel && !confidenceLabel) {
            return null;
        }
        return (
            <div className={componentStyles.ticketPropContext}>
                {marketLabel && (
                    <span className={componentStyles.ticketMarketLabel}>{marketLabel}</span>
                )}
                {confidenceLabel && (
                    <span
                        className={componentStyles.ticketConfidenceBadge}
                        style={{ backgroundColor: getConfidenceColor(bet.confidence) }}
                    >
                        {confidenceLabel}
                    </span>
                )}
            </div>
        );
    };

    // Same market + confidence block for the expanded detailed parlay card layout.
    const renderMarketAndConfidenceNormal = (bet) => {
        const marketLabel = bet.marketType ? formatPropMarketTypeLabel(bet.marketType) : null;
        const confidenceLabel = bet.confidence ? getConfidenceText(bet.confidence) : null;
        if (!marketLabel && !confidenceLabel) {
            return null;
        }
        return (
            <div className={componentStyles.betMarketConfidenceRow}>
                {marketLabel && (
                    <span className={componentStyles.betMarketTypeLabel}>{marketLabel}</span>
                )}
                {confidenceLabel && (
                    <span
                        className={componentStyles.betConfidenceBadge}
                        style={{ backgroundColor: getConfidenceColor(bet.confidence) }}
                    >
                        {confidenceLabel}
                    </span>
                )}
            </div>
        );
    };

    /**
     * Renders a simplified ticket view of a parlay
     * Clean, streamlined format with essential information (no explanations)
     * Shows player info for prop bets as it's core information
     */
    const renderSimplifiedView = (parlay, index) => {
        return (
            <div key={index} className={componentStyles.parlayTicket}>
                <div 
                    className={componentStyles.ticketHeader}
                    onClick={() => setExpandedParlay(expandedParlay === index ? null : index)}
                >
                    <div className={componentStyles.ticketHeaderLeft}>
                        <span className={componentStyles.ticketNumber}>#{index + 1}</span>
                        <span className={componentStyles.ticketLegs}>{parlay.num_legs} Legs</span>
                    </div>
                    <div className={componentStyles.ticketHeaderRight}>
                        <button
                            type="button"
                            className={componentStyles.buildSlipButton}
                            onClick={(event) => {
                                event.stopPropagation();
                                setBetSlipParlayIndex(index);
                            }}
                        >
                            Build Slip
                        </button>
                        <span className={componentStyles.expandIndicator}>
                            {expandedParlay === index ? '▼' : '▶'}
                        </span>
                    </div>
                </div>
                
                {expandedParlay === index && (
                    <div className={componentStyles.ticketBody}>
                        {parlay.bets.map((bet, betIndex) => {
                            // Check if this is a prop bet (has player or marketType indicates prop)
                            const isPropBet = bet.player || bet.marketType === 'prop' || (bet.type && bet.type.toLowerCase().includes('prop'));
                            const metaParts = buildMetaParts(bet);
                            
                            return (
                                <div key={betIndex} className={componentStyles.ticketLeg}>
                                    <div className={componentStyles.ticketLegMain}>
                                        <div className={componentStyles.ticketBetText}>
                                            {bet.bet}
                                        </div>
                                        {renderMarketAndConfidence(bet)}
                                        {isPropBet && bet.player && (
                                            <div className={componentStyles.ticketPlayer}>
                                                {bet.player}
                                            </div>
                                        )}
                                        {bet.event && (
                                            <div className={componentStyles.ticketEventInfo}>
                                                {bet.event}
                                            </div>
                                        )}
                                    </div>
                                    {metaParts.length > 0 && (
                                        <div className={componentStyles.ticketLegMeta}>
                                            {metaParts.map((part, partIndex) => (
                                                <span key={partIndex} className={componentStyles.ticketMetaItem}>
                                                    {part}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    /**
     * Renders the normal detailed view of a parlay
     * Shows all information including explanations, dates, players, etc.
     */
    const renderNormalView = (parlay, index) => {
        return (
            <div key={index} className={componentStyles.parlayCard}>
                <div 
                    className={componentStyles.parlayHeader}
                    onClick={() => setExpandedParlay(expandedParlay === index ? null : index)}
                >
                    <div className={componentStyles.parlayHeaderLeft}>
                        <span className={componentStyles.parlayNumber}>Parlay #{index + 1}</span>
                        <span className={componentStyles.parlayLegs}>{parlay.num_legs} Legs</span>
                    </div>
                    <div className={componentStyles.parlayHeaderRight}>
                        <button
                            type="button"
                            className={componentStyles.buildSlipButton}
                            onClick={(event) => {
                                event.stopPropagation();
                                setBetSlipParlayIndex(index);
                            }}
                        >
                            Build Slip
                        </button>
                        <span className={componentStyles.expandIndicator}>
                            {expandedParlay === index ? '▼' : '▶'}
                        </span>
                    </div>
                </div>
                
                {expandedParlay === index && (
                    <div className={componentStyles.parlayBody}>
                        {parlay.bets.map((bet, betIndex) => (
                            <div key={betIndex} className={componentStyles.betLeg}>
                                <div className={componentStyles.betLegHeader}>
                                    <span className={componentStyles.betType}>{bet.type}</span>
                                    <span className={componentStyles.betSport}>{bet.sport}</span>
                                </div>
                                <div className={componentStyles.betContent}>
                                    <div className={componentStyles.betRecommendation}>
                                        {bet.bet}
                                    </div>
                                    {renderMarketAndConfidenceNormal(bet)}
                                    {bet.player && (
                                        <div className={componentStyles.betPlayer}>
                                            Player: {bet.player}
                                        </div>
                                    )}
                                    <div className={componentStyles.betEvent}>
                                        {bet.event}
                                    </div>
                                    <div className={componentStyles.betDate}>
                                        {formatDate ? formatDate(bet.date) : bet.date}
                                    </div>
                                    {bet.explanation && (
                                        <div className={componentStyles.betExplanation}>
                                            {Array.isArray(bet.explanation) 
                                                ? bet.explanation.map((exp, expIndex) => (
                                                    <p key={expIndex}>{exp}</p>
                                                ))
                                                : <p>{bet.explanation}</p>
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (parlays.length === 0) {
        return (
            <div className={componentStyles.emptyState}>
                <p>No parlays generated. Try adjusting your configuration or wait for more events to be available.</p>
            </div>
        );
    }

    return (
        <>
            <div className={componentStyles.parlaysList}>
                {parlays.map((parlay, index) => 
                    viewMode === 'simplified' 
                        ? renderSimplifiedView(parlay, index)
                        : renderNormalView(parlay, index)
                )}
            </div>
            {betSlipParlayIndex !== null && parlays[betSlipParlayIndex] && (
                <ParlayBetSlipModal
                    parlay={parlays[betSlipParlayIndex]}
                    parlayIndex={betSlipParlayIndex}
                    onClose={() => setBetSlipParlayIndex(null)}
                />
            )}
        </>
    );
};

export default ParlayView;
